import { useState, useContext } from "react";
import { Form } from "../../../../layout/FormLayout";
import {
  useStoreState,
  convertUserToOption,
  useFormWrapper,
} from "../../../../util/util";
import type { Tenant, User, AccountSubscriber } from "../../../../types/types";
import {
  ButtonContainer,
  DeleteButton,
  PrimaryButtonLargeV2,
  SecondaryButtonWithPlusIcon,
  TertiaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import Axios from "axios";
import { endpoints } from "../../../../endpoints";
import { FormContainer } from "./CustomerSettings";
import { t } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { zodSelectBoxDefault } from "../../../../util/zod.util";
import * as zod from "zod";
import { SelectWithDeleteButton } from "../../../../layout/shared";
import { Controller, useFieldArray } from "react-hook-form";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { spacingTokens } from "../../../../designTokens";

type FormOutput = {
  subscribers: { label: string; value: string }[];
};

export const EditAccountSubscribers = ({
  buyerTenant,
  loggedInUser,
  refreshSettingsData,
  doneEditing,
  subscribers,
  accountManager,
  customerServiceRep,
}: {
  buyerTenant: Tenant;
  loggedInUser: User;
  accountManager?: User;
  customerServiceRep?: User;
  refreshSettingsData: () => void;
  doneEditing: () => void;
  subscribers: AccountSubscriber[];
}) => {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const EditSubscribersSchema = zod.object({
    subscribers: zod.array(zodSelectBoxDefault(t)),
  });

  const { handleSubmit, control, errors, formState, watch } =
    useFormWrapper<FormOutput>({
      resolver: zodResolver(EditSubscribersSchema),
      shouldUnregister: false,
      defaultValues: {
        subscribers:
          subscribers.length > 0
            ? subscribers.map((subscriber) => ({
                value: subscriber.id,
                label: `${subscriber.firstname} ${subscriber.lastname} - ${subscriber.email_address}`,
              }))
            : [{ label: undefined, value: undefined }],
      },
    });

  const methodsOfUseFieldArray = useFieldArray({
    control: control,
    name: "subscribers",
  });

  const { fields, append, remove } = methodsOfUseFieldArray;

  const fieldsValues = watch("subscribers");

  const onSubmit = async (formData: FormOutput) => {
    setIsSubmitting(true);
    try {
      const postBody = {
        subscribers: formData.subscribers
          .filter((field) => field.value)
          .map((field) => {
            return { id: field.value };
          }),
      };

      await Axios.patch(
        endpoints.v1_storefronts_id_tenants_id_customers_id_settings(
          storefront_id,
          loggedInUser.tenant_id,
          buyerTenant.id
        ),
        postBody
      );
      notifySuccess("Changes saved");
      setIsSubmitting(false);
      refreshSettingsData();
      doneEditing();
    } catch (error) {
      notifyError("There was an error submitting the changes", { error });
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {fields.map((field, index) => (
          <Controller
            key={field.id}
            control={control}
            name={`subscribers[${index}]`}
            render={({ onChange }) => (
              <SelectWithDeleteButton>
                <SearchSelectInfiniteScroll
                  name={`subscribers[${index}]`}
                  error={
                    errors?.subscribers
                      ? errors?.subscribers[index]?.value
                      : undefined
                  }
                  errors={errors}
                  formState={formState}
                  placeholder={t(`User`)}
                  baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                    storefront_id,
                    loggedInUser.tenant_id
                  )}
                  getOptions={(response: User[]) => {
                    return response
                      .filter(
                        (user) =>
                          user?.is_active &&
                          user.id !== accountManager?.id &&
                          user.id !== customerServiceRep?.id &&
                          !(fieldsValues ?? [])
                            .filter(Boolean)
                            .find((field) => field.value === user.id)
                      )
                      .map((user) => convertUserToOption(user));
                  }}
                  params={
                    new URLSearchParams({
                      order_by: "asc",
                      is_active: "true",
                      id: field.id ?? `${index}`, // add distinguishing param
                    })
                  }
                  onChange={onChange}
                  defaultValue={
                    field.value
                      ? { value: field.value, label: field.label }
                      : { value: undefined, label: undefined }
                  }
                  testid={`subscribers${index}`}
                />
                <DeleteButton
                  testid={`delete-button-${index}`}
                  onClick={() => remove(index)}
                  type="button"
                  height={20}
                  width={20}
                />
              </SelectWithDeleteButton>
            )}
            error={
              errors?.subscribers
                ? errors?.subscribers[index]?.value
                : undefined
            }
            errors={errors}
            formState={formState}
            defaultValue={
              field.value
                ? { value: field.value, label: field.label }
                : { value: undefined, label: undefined }
            }
          />
        ))}
        <SecondaryButtonWithPlusIcon
          type="button"
          onClick={() =>
            append({
              label: undefined,
              value: undefined,
            })
          }
        >
          {t("Add")}
        </SecondaryButtonWithPlusIcon>
        <ButtonContainer style={{ marginTop: spacingTokens.spacing_3xl }}>
          <TertiaryButtonLargeV2 onClick={doneEditing}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 loading={isSubmitting}>
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
};
